import React, { useState, useEffect } from 'react';
import { Toaster } from 'sonner';
import { Users, BarChart3, ClipboardList, Upload, Settings } from 'lucide-react';
import { CollaborationsProvider } from './context/CollaborationsContext';
import { LOGO_URL } from './config';
import Header from './components/Header';
import Toolbar from './components/Toolbar';
import CollaborationGrid from './components/CollaborationGrid';
import VentasPage from './components/VentasPage';
import FormulariosPage from './components/FormulariosPage';
import AjustesModal from './components/AjustesModal';
import ImportModal from './components/ImportModal';
import LoginScreen from './components/LoginScreen';
import FormularioPublico from './components/FormularioPublico';
import { checkAuth, isAuthRequired } from './auth';
import { cn } from './lib/utils';
import './styles/tailwind.css';

function SidebarLink({ icon: Icon, label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
        active
          ? 'bg-accent text-accent-foreground font-medium'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </button>
  );
}

function AppSidebar({ page, onPage, onOpenImport, onOpenSettings }) {
  return (
    <aside className="hidden md:flex sticky top-0 h-screen w-60 shrink-0 flex-col border-r bg-background">
      <div className="h-14 flex items-center px-6 border-b">
        <button
          type="button"
          onClick={() => onPage('influencers')}
          className="flex items-center"
        >
          <img
            src={LOGO_URL}
            alt="Logotipo"
            className="h-7 w-auto"
          />
        </button>
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        <SidebarLink
          icon={Users}
          label="Influencers"
          active={page === 'influencers'}
          onClick={() => onPage('influencers')}
        />
        <SidebarLink
          icon={BarChart3}
          label="Ventas"
          active={page === 'ventas'}
          onClick={() => onPage('ventas')}
        />
        <SidebarLink
          icon={ClipboardList}
          label="Formularios"
          active={page === 'formularios'}
          onClick={() => onPage('formularios')}
        />
        <SidebarLink icon={Upload} label="Importar ventas" onClick={onOpenImport} />
      </nav>

      <div className="p-3 border-t">
        <SidebarLink icon={Settings} label="Ajustes" onClick={onOpenSettings} />
      </div>
    </aside>
  );
}

// Secciones que se pueden abrir desde fuera con un enlace (#formularios), para
// que los correos puedan apuntar a una pantalla concreta y no solo a la portada.
const SECCIONES = ['influencers', 'ventas', 'formularios'];

function seccionDeLaUrl() {
  const pedida = window.location.hash.replace(/^#/, '').toLowerCase();
  return SECCIONES.includes(pedida) ? pedida : 'influencers';
}

function AppContent() {
  const [page, setPage] = useState(seccionDeLaUrl);

  // Mantiene el enlace y los botones atrás/adelante en sintonía con la sección.
  useEffect(() => {
    const alCambiarHash = () => setPage(seccionDeLaUrl());
    window.addEventListener('hashchange', alCambiarHash);
    return () => window.removeEventListener('hashchange', alCambiarHash);
  }, []);

  function irA(seccion) {
    setPage(seccion);
    if (seccionDeLaUrl() !== seccion) window.location.hash = seccion;
  }
  const [columns, setColumns] = useState(4);
  const [showAjustesModal, setShowAjustesModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  return (
    <div className="min-h-screen flex">
      <AppSidebar
        page={page}
        onPage={irA}
        onOpenImport={() => setShowImportModal(true)}
        onOpenSettings={() => setShowAjustesModal(true)}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <main className="flex-1 p-6 max-w-7xl w-full mx-auto">
          {page === 'influencers' && (
            <div className="space-y-6">
              <Header />
              <Toolbar columns={columns} onColumnsChange={setColumns} />
              <CollaborationGrid columns={columns} />
            </div>
          )}
          {page === 'ventas' && <VentasPage />}
          {page === 'formularios' && <FormulariosPage />}
        </main>
      </div>

      {showAjustesModal && <AjustesModal onClose={() => setShowAjustesModal(false)} />}
      {showImportModal && <ImportModal onClose={() => setShowImportModal(false)} />}
    </div>
  );
}

// El panel: todo lo que hay detrás del login.
function Panel() {
  // null = aún comprobando si el backend exige login
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      const required = await isAuthRequired();
      if (!active) return;
      // Si el backend no exige login, entrar directo; si lo exige, validar token local.
      setAuthed(required ? checkAuth() : true);
    })();
    return () => { active = false; };
  }, []);

  if (authed === null) return null;
  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;
  return (
    <CollaborationsProvider>
      <AppContent />
    </CollaborationsProvider>
  );
}

// /formulario es público: lo rellenan los influencers, sin sesión. Se decide
// por la ruta antes de montar nada del panel, para no pedir login ni cargar
// colaboraciones a quien solo viene a rellenar el formulario.
function esFormularioPublico() {
  return window.location.pathname.replace(/\/+$/, '') === '/formulario';
}

export default function App() {
  return (
    <>
      {esFormularioPublico() ? <FormularioPublico /> : <Panel />}
      <Toaster position="top-right" richColors />
    </>
  );
}
